import { z } from 'zod';
import { idSchema, isoDateTimeSchema, tenantIdSchema } from './common.schema';
/**
 * UsageSnapshot — daily per-tenant / per-client metered counts
 * (PRD §2.12 — P0 regardless of pricing model). Doubles as billing input
 * and abuse limiter (tech-arch §5.2 cost guardrail).
 */
export const usageSnapshotSchema = z
    .object({
    tenantId: tenantIdSchema,
    /** null = tenant-level rollup row; set = per-client row. */
    clientId: idSchema.nullable(),
    /** Snapshot day, YYYY-MM-DD (tenant-timezone-agnostic: UTC day).
     *  Validated as a real calendar date — billing input must not accept
     *  impossible days like 2026-02-31. */
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .refine((d) => {
        const parsed = new Date(`${d}T00:00:00.000Z`);
        return (!Number.isNaN(parsed.getTime()) &&
            parsed.toISOString().slice(0, 10) === d);
    }, { message: 'must be a real calendar date (YYYY-MM-DD)' }),
    resourceCounts: z
        .object({
        total: z.number().int().nonnegative(),
        /** Exhaustive breakdown — every kind present (0 allowed), so
         *  billing/reporting never sees a partial breakdown. */
        byKind: z
            .object({
            device: z.number().int().nonnegative(),
            website: z.number().int().nonnegative(),
            cloud: z.number().int().nonnegative(),
        })
            .strict(),
    })
        .strict(),
    activeCollectors: z.number().int().nonnegative(),
    /** Datapoints ingested during the day. */
    datapointsIngested: z.number().int().nonnegative(),
    /** Average ingest rate over the day (datapoints/second). */
    avgDatapointsPerSecond: z.number().finite().nonnegative(),
    computedAt: isoDateTimeSchema,
})
    .strict()
    .superRefine((val, ctx) => {
    // Billing records must be internally consistent: the aggregate must
    // equal the sum of its parts.
    const byKindSum = Object.values(val.resourceCounts.byKind).reduce((acc, n) => acc + (n ?? 0), 0);
    if (byKindSum !== val.resourceCounts.total) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['resourceCounts', 'total'],
            message: `total (${val.resourceCounts.total}) must equal the sum of byKind (${byKindSum})`,
        });
    }
});
//# sourceMappingURL=usage.schema.js.map